import { useState } from 'react'
import { X, Search, Lock, ChevronRight } from 'lucide-react'
import type { CatalogComponent } from '@/types/components'
import { LivePreviewIframe } from '@/components/gallery/live-preview-iframe'
import { CategoryFolderCard } from '@/components/gallery/category-folder-card'

interface GalleryModalProps {
  open: boolean
  onClose: () => void
  onSelect: (component: CatalogComponent) => void
  components: CatalogComponent[]
  isLoading?: boolean
}

const UPGRADE_TAB = '__upgrade__'

export function GalleryModal({ open, onClose, onSelect, components, isLoading }: GalleryModalProps) {
  const [activeCategory, setActiveCategory] = useState<string>('all')
  const [search, setSearch] = useState('')

  if (!open) return null

  const available = components.filter((c) => !c.locked)
  const locked = components.filter((c) => c.locked)

  const categories = [
    { id: 'all', label: 'Semua' },
    ...Array.from(new Set(available.map((c) => c.category))).map((cat) => ({
      id: cat,
      label: cat,
    })),
    ...(locked.length > 0 ? [{ id: UPGRADE_TAB, label: 'Upgrade Plan' }] : []),
  ]

  const isUpgradeTab = activeCategory === UPGRADE_TAB

  // Show category folder cards when on "Semua" tab with no active search
  const showFolderView = activeCategory === 'all' && !search.trim()

  const filtered = isUpgradeTab
    ? locked.filter((c) => {
        if (!search.trim()) return true
        const q = search.toLowerCase()
        return c.label.toLowerCase().includes(q) || c.description.toLowerCase().includes(q)
      })
    : available.filter((c) => {
        const matchCategory = activeCategory === 'all' || c.category === activeCategory
        if (!matchCategory) return false
        if (!search.trim()) return true
        const q = search.toLowerCase()
        return c.label.toLowerCase().includes(q) || c.description.toLowerCase().includes(q)
      })

  function handleSelect(def: CatalogComponent) {
    onSelect(def)
    onClose()
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" />

      {/* Modal */}
      <div
        className="relative z-10 w-full max-w-3xl max-h-[85vh] flex flex-col rounded-t-2xl sm:rounded-2xl border border-border bg-card shadow-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-border">
          <h2 className="font-semibold text-base flex-1">Tambah Komponen</h2>
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cari komponen..."
              className="w-full rounded-md border border-input bg-background pl-8 pr-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          <button
            onClick={onClose}
            className="rounded-md p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Category tabs */}
        <div className="flex gap-1 px-5 py-2 border-b border-border overflow-x-auto">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`shrink-0 rounded-md px-3 py-1 text-xs font-medium transition-colors ${
                activeCategory === cat.id
                  ? cat.id === UPGRADE_TAB
                    ? 'bg-amber-500 text-white'
                    : 'bg-primary text-primary-foreground'
                  : cat.id === UPGRADE_TAB
                    ? 'text-amber-600 hover:bg-amber-50'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
            >
              {cat.id === UPGRADE_TAB && <Lock className="inline h-3 w-3 mr-1 -mt-0.5" />}
              {cat.label}
              {cat.id === UPGRADE_TAB && (
                <span className="ml-1 opacity-70">{locked.length}</span>
              )}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {isLoading ? (
            <p className="text-sm text-muted-foreground text-center py-8">Memuat komponen...</p>
          ) : showFolderView ? (
            /* Folder view — "Semua" tab with no search */
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {categories
                .filter((cat) => cat.id !== 'all' && cat.id !== UPGRADE_TAB)
                .map((cat) => (
                  <CategoryFolderCard
                    key={cat.id}
                    category={cat.id}
                    components={available.filter((c) => c.category === cat.id)}
                    onClick={() => setActiveCategory(cat.id)}
                  />
                ))}
              {locked.length > 0 && (
                <button
                  onClick={() => setActiveCategory(UPGRADE_TAB)}
                  className="group w-full text-left rounded-xl border border-amber-200 bg-amber-50/40 hover:bg-amber-50 p-4 transition-all duration-150 hover:shadow-md"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="shrink-0 rounded-lg p-2 bg-amber-100 text-amber-600">
                      <Lock className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-sm text-amber-700">Upgrade Plan</h3>
                      <p className="text-xs text-amber-600/70">{locked.length} komponen terkunci</p>
                    </div>
                    <ChevronRight className="shrink-0 h-4 w-4 text-amber-500 group-hover:translate-x-0.5 transition-transform" />
                  </div>
                  <p className="text-xs text-amber-600/70">Upgrade tier untuk mengakses komponen premium.</p>
                </button>
              )}
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              Tidak ada komponen yang cocok.
            </p>
          ) : (
            /* Component grid — specific category or search active */
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {filtered.map((def) =>
                isUpgradeTab ? (
                  <div
                    key={def.component_key}
                    className="relative rounded-xl border border-border bg-background overflow-hidden opacity-60"
                  >
                    <LivePreviewIframe
                      componentKey={def.component_key}
                      defaultSettings={def.default_settings}
                      faded
                    />
                    <div className="p-3">
                      <p className="text-xs font-semibold text-foreground leading-tight">{def.label}</p>
                      {def.data_requirement && (
                        <span className="inline-block mt-0.5 text-[9px] uppercase tracking-wide bg-primary/10 text-primary px-1.5 py-0.5 rounded font-medium">
                          {def.data_requirement}
                        </span>
                      )}
                      <p className="mt-1 text-[10px] text-muted-foreground line-clamp-2 leading-tight">
                        {def.description}
                      </p>
                    </div>
                    <div className="absolute inset-0 flex flex-col items-center justify-center rounded-xl bg-background/60 backdrop-blur-[1px]">
                      <Lock className="h-4 w-4 text-amber-500 mb-1" />
                      <span className="text-[10px] font-semibold text-amber-600">Upgrade Plan</span>
                    </div>
                  </div>
                ) : (
                  <button
                    key={def.component_key}
                    onClick={() => handleSelect(def)}
                    className="text-left rounded-xl border border-border bg-background overflow-hidden transition-colors hover:border-primary hover:bg-primary/5 cursor-pointer group"
                  >
                    <LivePreviewIframe
                      componentKey={def.component_key}
                      defaultSettings={def.default_settings}
                    />
                    <div className="p-3">
                      <p className="text-xs font-semibold text-foreground leading-tight">{def.label}</p>
                      {def.data_requirement && (
                        <span className="inline-block mt-0.5 text-[9px] uppercase tracking-wide bg-primary/10 text-primary px-1.5 py-0.5 rounded font-medium">
                          {def.data_requirement}
                        </span>
                      )}
                      <p className="mt-1 text-[10px] text-muted-foreground line-clamp-2 leading-tight">
                        {def.description}
                      </p>
                    </div>
                  </button>
                )
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
